import React, { createContext, useContext, useState, useEffect } from 'react';
import { Alert } from '@mui/material';

const ErrorContext = createContext();

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);

  const addError = (error) => {
    const newError = {
      id: Date.now() + Math.random(), // Unique ID for the error
      message: error.message || error,
      type: error.type || 'error',
      timestamp: new Date(),
      source: error.source || 'general'
    };
    
    setErrors(prevErrors => [...prevErrors, newError]);
  };

  const removeError = (id) => {
    setErrors(prevErrors => prevErrors.filter(error => error.id !== id));
  };

  const clearErrors = () => {
    setErrors([]);
  };

  // Listen for global API error events
  useEffect(() => {
    const handleApiError = (event) => {
      addError({
        message: event.detail.message,
        type: event.detail.type || 'error',
        source: event.detail.source || 'api'
      });
    };

    window.addEventListener('apiError', handleApiError);

    return () => {
      window.removeEventListener('apiError', handleApiError);
    };
  }, []);

  const value = {
    errors,
    addError,
    removeError,
    clearErrors
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      {errors.map((error) => (
        <Alert
          key={error.id}
          severity={error.type === 'warning' ? 'warning' : error.type === 'info' ? 'info' : 'error'}
          onClose={() => removeError(error.id)}
          sx={{ 
            position: 'fixed', 
            top: '20px', 
            right: '20px', 
            zIndex: 9999,
            maxWidth: '400px',
            wordBreak: 'break-word'
          }}
        >
          {error.message}
        </Alert>
      ))}
    </ErrorContext.Provider>
  );
};